import { Dashboard as DashboardIcon, SwapHoriz as TradingIcon, Assessment as ReportIcon, Gavel as ComplianceIcon, History as HistoricalIcon, Analytics as AnalyticsIcon, AccountBalance as FoFIcon, TrendingUp as PerformanceIcon, Description as TemplateIcon, Storage as DataIcon, FolderOpen as PortfolioIcon, Settings as SettingsIcon, Help as HelpIcon, VerifiedUser as DataQualityIcon, Security as RiskIcon, AutoGraph as OptimizationIcon, AttachMoney as FeeIcon, Speed as OMSIcon, AccountBalanceWallet as CollateralIcon, PlaylistPlay as BatchIcon, Receipt as PostTradeIcon, ShowChart as PricingIcon, Hub as IntegrationIcon, ManageAccounts as IdentityIcon, Timeline as MarketDataIcon, TrendingUp as AnrTrendingUpIcon, Verified as AnrVerifiedIcon, CompareArrows as AnrCompareArrowsIcon, AltRoute as AnrAltRouteIcon, PieChart as AnrPieChartIcon, ScatterPlot as AnrScatterPlotIcon, FactCheck as AnrFactCheckIcon, Description as AnrDescriptionIcon, Policy as AnrPolicyIcon, BarChart as AnrSectionIcon } from '@/components/ui/qicons';
import React from 'react';

export interface NavigationItem {
  label: string;
  path: string;
  icon?: React.ReactElement;
  children?: NavigationItem[];
}

export interface NavigationSection {
  id: string;
  label: string;
  icon: React.ReactElement;
  children: NavigationItem[];
}

export const navigationConfig: NavigationSection[] = [
  {
    id: 'main-view',
    label: 'Main View',
    icon: <DashboardIcon />,
    children: [
      { label: 'Dashboard', path: '/dashboard' },
      { label: 'Tracking Metrics', path: '/main-view/tracking-metrics' },
      { label: 'Usage', path: '/main-view/usage' },
      { label: 'Portfolio View', path: '/portfolio-view' },
    ],
  },
  {
    id: 'trades-orders',
    label: 'Trades/Orders',
    icon: <TradingIcon />,
    children: [
      { label: 'Trading Workbench', path: '/trading/workbench' },
      { label: 'Trade Browser', path: '/trading' },
      { label: 'Orders & Trades', path: '/activity/equity-orders' },
      {
        label: 'Fixed Income',
        path: '/trading/workbench?type=bond',
        children: [
          { label: 'Bond Trade', path: '/trading/workbench?type=bond' },
          { label: 'Repo Trade', path: '/activity/repo-trade' },
          { label: 'Sec Lending', path: '/activity/sec-lending' },
          { label: 'Loan/Deposit Trade', path: '/activity/loan-deposit-trade' },
          { label: 'FRA Trade', path: '/trading/workbench?type=fra' },
          { label: 'Bond Pick Engine', path: '/trading/bond-pick' },
        ],
      },
      {
        label: 'Foreign Exchange',
        path: '/trading/workbench?type=fx',
        children: [
          { label: 'FX Trade Capture', path: '/trading/workbench?type=fx' },
          { label: 'FX Spot', path: '/trading/fx-spot' },
          { label: 'FX Forward', path: '/trading/fx-forward' },
          { label: 'FX Swap', path: '/trading/fx-swap' },
          { label: 'FX Options', path: '/trading/workbench?type=option' },
          { label: 'FX NDF', path: '/trading/workbench?type=fx' },
          // FXCCS sunset: cross-currency swaps are now an IRS swap_type.
          // Use Interest Rates → IRS Trade and pick "Cross-Currency".
        ],
      },
      {
        label: 'Interest Rates',
        path: '/trading/workbench?type=swap',
        children: [
          { label: 'IRS Trade', path: '/trading/workbench?type=swap' },
          { label: 'FRA Trade', path: '/trading/workbench?type=fra' },
          { label: 'Cap/Floor/Collar', path: '/trading/workbench?type=capfloor' },
          { label: 'CMS Trade', path: '/trading/workbench?type=swap' },
          { label: 'Swaption Trade', path: '/trading/workbench?type=swaption' },
          { label: 'Cash Transfer', path: '/activity/cash-transfer-trade' },
        ],
      },
      {
        label: 'Equity & Derivatives',
        path: '/trading/workbench?type=equity',
        children: [
          { label: 'Equity Trade', path: '/trading/workbench?type=equity' },
          { label: 'Listed Options', path: '/trading/workbench?type=option' },
          { label: 'OTC Options', path: '/trading/workbench?type=option' },
          { label: 'Futures Trade', path: '/activity/futures-trade' },
          { label: 'Futures Margin Calls', path: '/trading/futures-margin-calls' },
          { label: 'Futures Position Limits', path: '/trading/futures-position-limits' },
          { label: 'CDS Trade', path: '/trading/workbench?type=cds' },
          { label: 'TRS Trade', path: '/trading/workbench?type=exotic' },
          // Repointed 2026-05-04 from /trading/investment (legacy
          // InvestmentTradingPage with broken /api/trades calls + lowercase
          // 'buy' direction + product_type-as-trade_type confusion) to the
          // working /trading/investment-instruments (Material-UI port wired
          // through proper /api/v1 endpoints). Old route is kept active for
          // bookmark backwards-compat but the broken page now shows a
          // deprecation banner pointing at the new path.
          { label: 'Investment Trade', path: '/trading/investment-instruments' },
        ],
      },
      {
        label: 'Reports',
        path: '/activity/trade',
        children: [
          { label: 'Trade Report', path: '/activity/trade' },
          { label: 'Trades CF Report', path: '/activity/trade-cf' },
          { label: 'Inventory Report', path: '/activity/inventory-report' },
          { label: 'Cashflow Ladder', path: '/activity/cashflow-ladder' },
        ],
      },
    ],
  },
  {
    id: 'oms',
    label: 'Order Management',
    icon: <OMSIcon />,
    children: [
      { label: 'Order Workspace', path: '/oms/workspace' },
      { label: 'Order Entry', path: '/oms/order-entry-professional' },
      { label: 'Trading Blotter', path: '/oms/blotter' },
      { label: 'Active Placements', path: '/oms/placements' },
      { label: 'Block Monitoring', path: '/oms/block-monitoring' },
      {
        label: 'Allocations',
        path: '/oms/allocations',
        children: [
          { label: 'Pro-Rata Allocation', path: '/oms/allocations/pro-rata' },
          { label: 'Manager Programs', path: '/oms/manager-programs' },
        ],
      },
      { label: 'Execution Analytics', path: '/oms/execution-analytics' },
      { label: 'Targeting', path: '/oms/targeting' },
      { label: 'Internal Crossing', path: '/oms/crossing' },
      { label: 'Contingent Orders', path: '/oms/contingent-orders' },
      { label: 'Hybrid Orders', path: '/oms/hybrid-orders' },
      { label: 'Manager Workbench', path: '/oms/workbench' },
      { label: 'Tax Lot Inventory', path: '/oms/tax-lots' },
      { label: 'Pledge & Collateral', path: '/oms/pledges' },
      { label: 'Cash Balances', path: '/oms/cash-balances' },
      { label: 'Trade Automation', path: '/oms/trade-automation' },
      { label: 'System Parameters', path: '/oms/system-parameters' },
    ],
  },
  {
    id: 'compliance',
    label: 'Compliance',
    icon: <ComplianceIcon />,
    children: [
      { label: 'Compliance Rules', path: '/activity/compliance/rules' },
      { label: 'Assign Rules to Portfolio', path: '/activity/compliance/assign-rules' },
      { label: 'Pre-Trade Breach', path: '/activity/compliance/pre-trade' },
      { label: 'Post-Trade Breach', path: '/activity/compliance/post-trade' },
      { label: 'Historical Reporting', path: '/activity/compliance/historical-reporting' },
      { label: 'Regulatory Reporting', path: '/activity/compliance/regulatory-reporting' },
      { label: 'Bond Regulatory', path: '/compliance/bond-regulatory' },
      { label: 'Equity MiFID II', path: '/compliance/equity-mifid' },
      { label: 'Short Position Reporting', path: '/compliance/short-position-reporting' },
      { label: 'Trade Surveillance', path: '/compliance/surveillance' },
      { label: 'Breach Management', path: '/compliance/breach' },
      { label: 'Audit Trail', path: '/compliance/audit-trail' },
      { label: 'Lifecycle Audit', path: '/compliance/lifecycle-audit' },
    ],
  },
  {
    id: 'performance-analytics',
    label: 'Performance Analytics',
    icon: <AnalyticsIcon />,
    children: [
      {
        label: 'Fund Analysis',
        path: '/analytics/nav-dashboard',
        children: [
          { label: 'NAV Dashboard', path: '/analytics/nav-dashboard' },
          { label: 'Metrics', path: '/performance/fund-parametrics' },
          { label: 'Total Return', path: '/performance/total-return' },
          { label: 'Tear Sheet (Engine)', path: '/performance/tear-sheet' },
          { label: 'Distribution (VaR)', path: '/performance/var-analysis' },
          { label: 'Fund Radar', path: '/data/financial-health-radar' },
          { label: 'Exposure', path: '/allocation/composition' },
          { label: 'P&L Dashboard', path: '/analytics/pnl-dashboard' },
          { label: 'Performance Metrics', path: '/analytics/performance-metrics' },
        ],
      },
      {
        label: 'Fund vs Benchmark',
        path: '/main-view/fund-benchmark',
        children: [
          { label: 'Comparison Dashboard', path: '/main-view/fund-benchmark' },
          { label: 'Custom Benchmark', path: '/fund/benchmark/custom-benchmark' },
          { label: 'Performance Comparison', path: '/performance/comparison' },
          { label: 'Statistical Metrics', path: '/fund/statistical-metrics' },
          { label: 'Analysis for a Period', path: '/performance/periods' },
          { label: 'Metrics by Period', path: '/historical/metrics-by-period' },
          { label: 'Multi-Fund Comparison', path: '/performance/multi-funds' },
          { label: 'Fund/Bench. Radar', path: '/performance/financial-health-radar' },
          { label: 'Fund/Benchmark Holdings', path: '/allocation/fund-benchmark-comparison' },
          { label: 'Downside Analysis', path: '/performance/downside' },
          { label: 'Risk-Adjusted Returns', path: '/performance/risk-adjusted' },
          { label: 'Rebased Charts', path: '/performance/rebased' },
        ],
      },
      {
        label: 'Attribution',
        path: '/attribution/brinson',
        children: [
          // Production-ready (real backend, lineage stamped, fail-loud governance)
          { label: 'Brinson', path: '/attribution/brinson' },
          { label: 'Campisi', path: '/attribution/campisi' },
          { label: 'FX Effect', path: '/attribution/fx-effect' },
          { label: 'Attribution Analysis', path: '/analytics/attribution-analysis' },
          { label: 'Return Contribution', path: '/allocation/return-contribution' },
          { label: 'Risk Contribution', path: '/allocation/risk-contribution' },
          // Single-period exposure decomposition (legacy)
          { label: 'Multi Factor (legacy)', path: '/attribution/multi-factor' },
          // NEW — real OLS time-series regression with R², t-stats, lineage
          { label: 'Multi Factor Regression', path: '/attribution/multi-factor-regression' },
          // "Fund Brinson" / "Fund FX Effect" entries removed — both were
          // <Navigate> aliases of the regular Brinson / FX Effect pages
          // with no fund-level rollup logic; misleading menu items.
          // Routes preserved in analyticsRoutes.tsx for any bookmarks.
        ],
      },
      {
        label: 'Scenarios',
        path: '/attribution/simulation/scenario-creation',
        children: [
          { label: 'Scenario Creation', path: '/attribution/simulation/scenario-creation' },
          { label: 'Scenario Analysis (engine)', path: '/analytics/scenario-analysis' },
          { label: 'Backtesting (engine)', path: '/analytics/backtesting' },
          { label: 'Scenario Result', path: '/attribution/simulation/scenario-result' },
        ],
      },
      {
        label: 'Historical & Charts',
        path: '/historical/graph',
        children: [
          { label: 'Graph One Asset', path: '/historical/graph' },
          { label: 'Graph Multi Asset', path: '/historical/asset-plus' },
          { label: 'Chart Analysis', path: '/historical/chart-analysis' },
          { label: 'Asset Analysis', path: '/historical/asset-analysis' },
          { label: 'Return Analysis', path: '/historical/return' },
          { label: 'Daily Return', path: '/historical/convert-daily-price' },
          { label: 'Daily Price to Monthly Return', path: '/historical/monthly-return-calc' },
        ],
      },
      {
        label: 'Reporting',
        path: '/template/builder',
        children: [
          { label: 'Template Builder', path: '/template/builder' },
          { label: 'Report Configurations', path: '/template/configure' },
          { label: 'Generate Report', path: '/template/generate' },
        ],
      },
      {
        label: 'PE/VC & FoF Metrics',
        path: '/fof/performance-analytics',
        children: [
          { label: 'IRR / TVPI / DPI / MOIC', path: '/fof/performance-analytics' },
          { label: 'TWRR Calculation', path: '/fof/twrr-calculation' },
          { label: 'MWRR Calculation', path: '/fof/mwrr-calculation' },
          { label: 'Underlying Funds', path: '/fof/underlying-funds' },
          { label: 'FoF Attribution', path: '/fof/attribution' },
          { label: 'FoF Exposure', path: '/fof/exposure-analysis' },
          // FoF Benchmarking removed — fake data (audit)
          { label: 'Cash Flow Graph', path: '/fof/cash-flow-graph' },
          { label: 'Backward Analysis', path: '/fof/simulation' },
          { label: 'Funds Universe', path: '/fof/fund-universe' },
        ],
      },
      {
        // Tier-1 governance plane shipped in v2 + A3.x.
        // Single landing place for reviewers auditing the policy +
        // sign-off + composite/benchmark/reporting/scenario lifecycle.
        label: 'Governance (Tier-1)',
        path: '/performance/methodologies',
        children: [
          { label: 'Methodology Register', path: '/performance/methodologies' },
          { label: 'Performance Runs', path: '/performance/runs' },
          { label: 'Performance Closes', path: '/performance/closes' },
          { label: 'GIPS Composites', path: '/performance/composites' },
          { label: 'Benchmark Governance', path: '/performance/benchmark-governance' },
          { label: 'Reporting Governance', path: '/performance/reporting' },
          { label: 'Stress Scenarios (v2)', path: '/performance/scenarios' },
        ],
      },
    ],
  },
  {
    id: 'pricing',
    label: 'Pricing Tools',
    icon: <PricingIcon />,
    children: [
      { label: 'Pricing Overview', path: '/data/pricing' },
      { label: 'Bond Calculator', path: '/data/pricing/bond-calculator' },
      { label: 'IRS Pricing', path: '/data/pricing/irs' },
      { label: 'FX Pricing', path: '/data/pricing/fx' },
      { label: 'Cap/Floor Pricing', path: '/data/pricing/cap-floor' },
      { label: 'Loan/Deposit Pricing', path: '/data/pricing/loan-deposit' },
      { label: 'Repo Pricing', path: '/data/pricing/repo' },
      { label: 'SecLend Pricing', path: '/data/pricing/sec-lend' },
    ],
  },
  {
    id: 'data',
    label: 'Data',
    icon: <DataIcon />,
    children: [
      {
        label: 'Static Data',
        path: '/data/static/company',
        children: [
          { label: 'Company', path: '/data/static/company' },
          { label: 'Company (Sci-Fi Demo)', path: '/data/static/company-scifi' },
          { label: 'Fund', path: '/data/static/fund' },
          { label: 'Benchmark', path: '/data/static/benchmark' },
          { label: 'Currency', path: '/data/static/currency' },
          { label: 'Country', path: '/data/static/country' },
          { label: 'Rating', path: '/data/static/rating' },
          { label: 'GICS', path: '/data/static/gics' },
          { label: 'Asset Type', path: '/data/static/asset-type' },
          { label: 'Region', path: '/data/static/region' },
          { label: 'Legal Entity', path: '/data/static/legal-entity' },
          { label: 'Geographic', path: '/data/static/geographic' },
          { label: 'Calendar', path: '/data/static/calendar' },
        ],
      },
      {
        label: 'Market Data',
        path: '/data/market/quote',
        children: [
          { label: 'Daily Quote', path: '/data/market/quote' },
          { label: 'Daily Return', path: '/data/market/daily-return' },
          { label: 'Monthly Return', path: '/data/market/monthly-return' },
          { label: 'Return Data', path: '/data/market/return-data' },
          { label: 'Price Data', path: '/data/market/price' },
          { label: 'Yield Tenor', path: '/data/market/yield-curve' },
          { label: 'Equity Building by Index', path: '/data/market/equity-building' },
          { label: 'Index Graph', path: '/data/market/equity-graph' },
          { label: 'FX Rates', path: '/data/market/fx-rates' },
          { label: 'Reference Rates (SOFR/ESTR/SONIA)', path: '/data/market/reference-rates' },
          { label: 'NAV History', path: '/data/market/nav-history' },
          { label: 'Restatement History', path: '/data/market/restatement-history' },
          { label: 'Vendor Data Status', path: '/data/market/vendor-data-status' },
          { label: 'CA Elections', path: '/data/ca/elections' },
          { label: 'CA Messages (seev.*)', path: '/data/ca/messages' },
        ],
      },
      {
        label: 'Reference Data',
        path: '/reference-data/sectors',
        children: [
          { label: 'Industry Sectors', path: '/reference-data/sectors' },
          { label: 'Geographic Regions', path: '/reference-data/regions' },
          { label: 'Asset Classes', path: '/reference-data/asset-classes' },
          { label: 'Debt Seniority', path: '/reference-data/seniority' },
          { label: 'CDS Doc Clauses', path: '/reference-data/doc-clauses' },
          { label: 'Counterparty Types', path: '/reference-data/counterparty-types' },
          { label: 'Tenors', path: '/reference-data/tenors' },
          { label: 'Day Count Conventions', path: '/reference-data/day-count-conventions' },
          { label: 'Transaction Types', path: '/reference-data/transaction-types' },
          { label: 'Exchanges', path: '/reference-data/exchanges' },
          { label: 'Credit Ratings', path: '/data/static/rating' },
          { label: 'Risk Factor Types', path: '/reference-data/risk-factor-types' },
          { label: 'Scenario Types', path: '/reference-data/scenario-types' },
          { label: 'Shock Types', path: '/reference-data/shock-types' },
          { label: 'Severity Levels', path: '/reference-data/severity-levels' },
          { label: 'Breach Status', path: '/reference-data/breach-status' },
        ],
      },
      {
        label: 'Yield Curves',
        path: '/data/market/curves-workbench',
        children: [
          { label: 'Curves Workbench', path: '/data/market/curves-workbench' },
          { label: 'Curve Builder', path: '/data/market/curve-builder' },
          { label: 'Credit Curve Builder', path: '/data/market/credit-curve-builder' },
          { label: 'Vol Surface Viewer', path: '/data/market/vol-surface-viewer' },
          { label: 'Tenor Comparison', path: '/curves/tenors-evol-comp' },
          { label: 'Curve Evolution', path: '/curves/curve-evolution' },
          { label: 'Curve Comparison', path: '/curves/curve-comparison' },
          { label: 'Curve Analysis', path: '/curves/curve-analysis' },
          // W1.7 (2026-07-14): '/curves/management' matched no route (the ONE dead nav link) — the
          // curve management surface is the CurvesWorkbench.
          { label: 'Yield Curve Management', path: '/data/market/curves-workbench' },
        ],
      },
      {
        label: 'Holding',
        path: '/data/holding/position',
        children: [
          { label: 'Position', path: '/data/holding/position' },
        ],
      },
      {
        label: 'Instrument',
        path: '/data/instrument/bonds',
        children: [
          { label: 'Bonds', path: '/data/instrument/bonds' },
          { label: 'Bond Convention Sets', path: '/data/bond-convention-sets' },
          { label: 'Equity', path: '/data/instrument/equity' },
          { label: 'Future', path: '/data/instrument/future' },
        ],
      },
      {
        label: 'Bond Accounting',
        path: '/data/bonds/coupon-schedule',
        children: [
          { label: 'Coupon Schedule', path: '/data/bonds/coupon-schedule' },
          { label: 'Amortization', path: '/data/bonds/amortization' },
          { label: 'Lifecycle Events', path: '/data/bonds/lifecycle-events' },
        ],
      },
      {
        label: 'Users',
        path: '/users/audit',
        children: [
          { label: 'Audit', path: '/users/audit' },
          { label: 'Admin', path: '/users/admin' },
        ],
      },
      {
        label: 'Data Quality',
        path: '/data-quality/dashboard',
        children: [
          { label: 'DQ Dashboard', path: '/data-quality/dashboard' },
          { label: 'Batch Management', path: '/data-quality/batches' },
          { label: 'Breach Investigation', path: '/data-quality/breaches' },
          { label: 'Validation Rules', path: '/data-quality/rules' },
          { label: 'Promotion Approvals', path: '/data-quality/promotions' },
          { label: 'Bond Data Quality', path: '/data/bond-data-quality' },
          { label: 'Equity Data Quality', path: '/data/equity-data-quality' },
        ],
      },
      { label: 'Day Count', path: '/data/daycount' },
      {
        label: 'Risk Data',
        path: '/risk-data/volatility-surface',
        children: [
          { label: 'Volatility Surface', path: '/risk-data/volatility-surface' },
          { label: 'CDS Curves', path: '/risk-data/cds-curves' },
          { label: 'PD Curves', path: '/risk-data/pd-curves' },
          { label: 'LGD Assumptions', path: '/risk-data/lgd' },
          { label: 'EAD/CCF Config', path: '/risk-data/ead-ccf' },
          { label: 'Credit Spreads', path: '/risk-data/credit-spreads' },
          { label: 'Recovery Rates', path: '/risk-data/recovery-rates' },
          { label: 'Correlation Matrix', path: '/risk-data/correlation-matrix' },
          { label: 'Options Greeks', path: '/risk-data/options-greeks' },
          // Reference Data hub link retired 2026-05-15. Reference data is
          // managed via the per-category pages under Data > Reference Data
          // (Industry Sectors / Geographic Regions / Asset Classes / etc).
        ],
      },
    ],
  },
  {
    id: 'batch-operations',
    label: 'Batch Operations',
    icon: <BatchIcon />,
    children: [
      { label: 'Control Tower', path: '/operations/control-tower' },
      { label: 'KPI Trends', path: '/operations/ops-trends' },
      { label: 'Operations Console', path: '/operations/batch-console' },
      { label: 'Task Station', path: '/operations/task-station' },
      { label: 'Operations Dashboard', path: '/operations/dashboard' },
      { label: 'Bulk Trade Processing', path: '/operations/bulk-processing' },
      { label: 'Trade Lifecycle', path: '/operations/trade-lifecycle' },
      { label: 'Workflow Rules', path: '/operations/workflow-rules' },
      { label: 'Bond Middle Office', path: '/operations/bond-middle-office' },
      { label: 'Bond CA Automation', path: '/operations/bond-ca-automation' },
      { label: 'CA Processing', path: '/operations/ca-processing' },
      { label: 'CA Bridge Pending', path: '/operations/ca-bridge' },
      { label: 'Activity Browser', path: '/operations/activity-browser' },
    ],
  },
  {
    id: 'risk-management',
    label: 'Risk Management',
    icon: <RiskIcon />,
    children: [
      { label: 'Risk Overview', path: '/risk-management' },
      { label: 'Market Risk', path: '/risk-management/market-risk-dashboard' },
      { label: 'Credit Risk', path: '/risk-management/credit-risk-dashboard' },
      { label: 'Liquidity Risk', path: '/risk-management/liquidity-risk-dashboard' },
      { label: 'Stress Testing', path: '/risk-management/stress-test-dashboard' },
      { label: 'Scenario Library', path: '/risk-management/scenario-library' },
      { label: 'Scenario Builder', path: '/risk-management/scenario-builder' },
      { label: 'Risk Factor Mapping', path: '/risk-management/risk-factor-mapping' },
      { label: 'Advanced Analytics', path: '/analytics/advanced' },
      { label: 'Risk Runs Console', path: '/risk-management/runs-console' },
      { label: 'Run Monitor', path: '/risk-management/run-monitor' },
      { label: 'Backtesting', path: '/risk-management/backtesting-dashboard' },
      { label: 'Reverse Stress Test', path: '/risk-management/reverse-stress-test' },
      { label: 'Counterparty Exposure', path: '/risk-management/counterparty-exposure' },
      { label: 'Bond Risk Dashboard', path: '/risk-management/bond-risk-dashboard' },
      { label: 'Equity Risk', path: '/risk-management/equity-risk' },
      { label: 'VaR Runs', path: '/risk-management/var-runs' },
      { label: 'Model Risk', path: '/risk-management/model-risk' },
      { label: 'XVA Dashboard', path: '/regulatory-risk/xva' },
      { label: 'ORE Run Console', path: '/regulatory-risk/ore-runs' },
      { label: 'Capital Dashboard', path: '/regulatory-risk/capital' },
      { label: 'P&L Attribution', path: '/regulatory-risk/pnl-attribution' },
    ],
  },
  {
    id: 'portfolio-optimization',
    label: 'Portfolio Optimization',
    icon: <OptimizationIcon />,
    children: [
      { label: 'Portfolio Optimization', path: '/optimization' },
      { label: 'Optimizer Run Console', path: '/optimization/run-console' },
      { label: 'Optimization Dashboard', path: '/optimization/dashboard' },
      { label: 'Rebalancing & Execution', path: '/optimization/rebalancing' },
      { label: 'Universes', path: '/optimization/universes' },
      { label: 'Run History', path: '/optimization/history' },
    ],
  },
  {
    id: 'fee-management',
    label: 'Fee Management',
    icon: <FeeIcon />,
    children: [
      { label: 'Fee Structures', path: '/fee-management/structures' },
      { label: 'Fund Assignments', path: '/fee-management/assignments' },
      { label: 'Fee Accruals', path: '/fee-management/accruals' },
      { label: 'Broker Commissions', path: '/fee-management/commissions' },
      { label: 'Exchange Fees', path: '/fee-management/exchange-fees' },
      { label: 'Tax & Withholding', path: '/fee-management/tax-withholding' },
      { label: 'Analytics', path: '/fee-management/analytics' },
      { label: 'Fee Configuration', path: '/fee-management/configuration' },
      { label: 'Fee Charges', path: '/fee-management/charges' },
      { label: 'Invoicing & Disputes', path: '/fee-management/invoicing' },
    ],
  },
  {
    id: 'multi-book',
    label: 'Multi-Book Architecture',
    icon: <PortfolioIcon />,
    children: [
      {
        label: 'IBOR (Investment Book)',
        path: '/multi-book/ibor',
        children: [
          { label: 'Real-time Positions', path: '/multi-book/ibor/positions' },
          { label: 'Pending Activities', path: '/multi-book/ibor/pending' },
          { label: 'Cash Projections', path: '/multi-book/ibor/cash-projections' },
        ],
      },
      {
        label: 'ABOR (Accounting Book)',
        path: '/multi-book/abor',
        children: [
          { label: 'Dashboard', path: '/multi-book/abor/dashboard' },
          { label: 'Valuations', path: '/multi-book/abor/valuations' },
          { label: 'NAV Calculation', path: '/multi-book/abor/nav' },
          { label: 'Trial Balance', path: '/multi-book/abor/trial-balance' },
        ],
      },
      {
        label: 'PBOR (Performance Book)',
        path: '/multi-book/pbor',
        children: [
          { label: 'Composites', path: '/multi-book/pbor/composites' },
          { label: 'GIPS Compliance', path: '/multi-book/pbor/gips' },
        ],
      },
      {
        label: 'RBOR (Risk Book)',
        path: '/multi-book/rbor',
        children: [
          { label: 'Risk Dashboard', path: '/multi-book/rbor/dashboard' },
          { label: 'Risk Snapshots', path: '/multi-book/rbor/snapshots' },
          { label: 'VaR History', path: '/multi-book/rbor/var-history' },
        ],
      },
      {
        label: 'Reconciliation',
        path: '/multi-book/recon',
        children: [
          { label: 'Dashboard', path: '/multi-book/recon/dashboard' },
          { label: 'Rules', path: '/multi-book/recon/rules' },
          { label: 'Breaks', path: '/multi-book/recon/breaks' },
          { label: 'Run Reconciliation', path: '/multi-book/recon/run' },
        ],
      },
      {
        label: 'General Ledger & Accounting',
        path: '/accounting/dashboard',
        children: [
          { label: 'General Ledger', path: '/accounting/dashboard' },
          { label: 'Accounting Rules', path: '/accounting/rules' },
          { label: 'EOD Processing', path: '/accounting/eod' },
          { label: 'Period-End & Closing', path: '/accounting/period-end' },
          { label: 'GL Export', path: '/accounting/gl-export' },
          { label: 'Trial Balance', path: '/accounting/trial-balance' },
          { label: 'Hedge Accounting', path: '/accounting/hedge' },
          { label: 'IFRS9 Impairment', path: '/accounting/ifrs9' },
        ],
      },
    ],
  },
  {
    id: 'post-trade',
    label: 'Post-Trade',
    icon: <PostTradeIcon />,
    children: [
      { label: 'SSI Management', path: '/post-trade/ssi-management' },
      { label: 'Settlement Transfers', path: '/post-trade/transfers' },
      { label: 'Settlement Report', path: '/post-trade/settlement-report' },
      { label: 'Settlement Fails', path: '/post-trade/settlement-fails' },
      { label: 'Bank Statement Entries', path: '/post-trade/bank-statement-entries' },
      { label: 'Cash Recon Breaks', path: '/post-trade/cash-recon-breaks' },
      { label: 'Netting Configuration', path: '/post-trade/netting-config' },
      { label: 'MX Message Configuration', path: '/post-trade/mx-config' },
      { label: 'MX Message Viewer', path: '/post-trade/mx-messages' },
      { label: 'MX Incoming Messages', path: '/post-trade/mx-incoming' },
      { label: 'Message Sender & Transport', path: '/post-trade/message-sender' },
      { label: 'Message Grouping', path: '/post-trade/message-grouping' },
      { label: 'Regulatory Codes', path: '/post-trade/regulatory-codes' },
      { label: 'Settlement Readiness', path: '/post-trade/settlement-readiness' },
      { label: 'MSG Workflow', path: '/post-trade/msg-workflow' },
    ],
  },
  {
    id: 'collateral-management',
    label: 'Collat. Mgmt',
    icon: <CollateralIcon />,
    children: [
      { label: 'Collateral Overview', path: '/collateral/overview' },
      { label: 'Security Pools', path: '/collateral/security-pools' },
      { label: 'Optimal Selection', path: '/collateral/selection' },
      { label: 'Substitution', path: '/collateral/substitution' },
      { label: 'Agreements (CSA/GMRA)', path: '/collateral/agreements' },
      { label: 'Collateral Dashboard', path: '/collateral/dashboard' },
      { label: 'Concentration Rules', path: '/collateral/concentration-rules' },
    ],
  },
  {
    id: 'integration',
    label: 'Integration',
    icon: <IntegrationIcon />,
    children: [
      { label: 'Configurations', path: '/integration/config' },
      { label: 'Data Import', path: '/integration/import' },
      { label: 'Data Export', path: '/integration/export' },
      { label: 'Activity Logs', path: '/integration/logs' },
    ],
  },
  {
    id: 'reporting',
    label: 'Reporting',
    icon: <ReportIcon />,
    children: [
      { label: 'Corporate Actions', path: '/reports/corporate-actions' },
      { label: 'Exercises / Expiries', path: '/reports/exercises-expiries' },
      { label: 'Early Terminations', path: '/reports/terminations' },
      { label: 'Realized P&L', path: '/reports/realized-pnl' },
      { label: 'Fix Reset Report', path: '/reports/fix-reset' },
      { label: 'Fees Report', path: '/reports/fees' },
      { label: 'Maturity Events', path: '/reports/maturity-events' },
      { label: 'P&L Reports', path: '/reports/pnl' },
    ],
  },
  // W1.2 decision: 3-level nesting used (NavigationItem.children supports it; existing config uses it e.g. Trades/Orders > Fixed Income > Bond Trade)
  // spec-3c-05-md W3 (2026-05-27): Market Data Service Tier 2→3 admin portal — 10 entities × 3 sub-groups
  // RBAC: data_admin + ops_admin + platform_admin (mapped to 'admin' role in hasPermission).
  {
    id: 'admin',
    label: 'Admin',
    icon: <SettingsIcon />,
    children: [
      {
        label: "Reference Data",
        path: '/admin/reference-data/accounts',
        children: [
        { label: "Account", path: '/admin/reference-data/accounts' },
        { label: "Accrual Convention", path: '/admin/reference-data/accrual-conventions' },
        { label: "Amortization Type", path: '/admin/reference-data/amortization-types' },
        { label: "Benchmark", path: '/admin/reference-data/benchmarks' },
        { label: "Bond Analytics Result", path: '/admin/reference-data/bond-analytics-results' },
        { label: "Bond Convention Set", path: '/admin/reference-data/bond-convention-sets' },
        { label: "Broker", path: '/admin/reference-data/brokers' },
        { label: "Broker Market Access", path: '/admin/reference-data/broker-market-accesses' },
        { label: "Business Day Convention", path: '/admin/reference-data/business-day-conventions' },
        { label: "CA Eligibility", path: '/admin/reference-data/ca-eligibilities' },
        { label: "CA Entitlement Forecast", path: '/admin/reference-data/ca-entitlement-forecasts' },
        { label: "CA Event", path: '/admin/reference-data/ca-events' },
        { label: "CA Event Terms", path: '/admin/reference-data/ca-event-termses' },
        { label: "CA Posting Batch", path: '/admin/reference-data/ca-posting-batches' },
        { label: "CA Posting Line", path: '/admin/reference-data/ca-posting-lines' },
        { label: "CA Recon Break", path: '/admin/reference-data/ca-recon-breaks' },
        { label: "Ca Break", path: '/admin/reference-data/ca-breaks' },
        { label: "Calendar", path: '/admin/reference-data/calendars' },
        { label: "Company", path: '/admin/reference-data/companies' },
        { label: "Compounding", path: '/admin/reference-data/compoundings' },
        { label: "Country", path: '/admin/reference-data/countries' },
        { label: "Coupon Type", path: '/admin/reference-data/coupon-types' },
        { label: "Cpi Interpolation Type", path: '/admin/reference-data/cpi-interpolation-types' },
        { label: "Credit Rating", path: '/admin/reference-data/credit-ratings' },
        { label: "Currency", path: '/admin/reference-data/currencies' },
        { label: "Currency Pair", path: '/admin/reference-data/currency-pairs' },
        { label: "Date Generation Rule", path: '/admin/reference-data/date-generation-rules' },
        { label: "Day Count Convention", path: '/admin/reference-data/day-count-conventions' },
        { label: "Desk", path: '/admin/reference-data/desks' },
        { label: "Distribution Type", path: '/admin/reference-data/distribution-types' },
        { label: "Duration Type", path: '/admin/reference-data/duration-types' },
        { label: "Equity Listing", path: '/admin/reference-data/equity-listings' },
        { label: "Exchange", path: '/admin/reference-data/exchanges' },
        { label: "Exercise Type", path: '/admin/reference-data/exercise-types' },
        { label: "Fund", path: '/admin/reference-data/funds' },
        { label: "Fx Pba Agreement", path: '/admin/reference-data/fx-pba-agreements' },
        { label: "GICS", path: '/admin/reference-data/gicses' },
        { label: "Geographic", path: '/admin/reference-data/geographics' },
        { label: "Holiday", path: '/admin/reference-data/holidays' },
        { label: "Index Constituent", path: '/admin/reference-data/index-constituents' },
        { label: "Inflation Index", path: '/admin/reference-data/inflation-indexes' },
        { label: "Instrument Type", path: '/admin/reference-data/instrument-types' },
        { label: "Interest Rate Index", path: '/admin/reference-data/interest-rate-indexes' },
        { label: "Issuer Rating", path: '/admin/reference-data/issuer-ratings' },
        { label: "Legal Entity", path: '/admin/reference-data/legal-entities' },
        { label: "Lifecycle Event Type", path: '/admin/reference-data/lifecycle-event-types' },
        { label: "Oms Broker", path: '/admin/reference-data/oms-brokers' },
        { label: "Option Settlement Type", path: '/admin/reference-data/option-settlement-types' },
        { label: "Option Type", path: '/admin/reference-data/option-types' },
        { label: "Party Role", path: '/admin/reference-data/party-roles' },
        { label: "Payment Frequency", path: '/admin/reference-data/payment-frequencies' },
        { label: "Rate Averaging Type", path: '/admin/reference-data/rate-averaging-types' },
        { label: "Rating Transition Matrix", path: '/admin/reference-data/rating-transition-matrixes' },
        { label: "Reference Obligation", path: '/admin/reference-data/reference-obligations' },
        { label: "Reference Rate Fixing", path: '/admin/reference-data/reference-rate-fixings' },
        { label: "Restructuring Clause", path: '/admin/reference-data/restructuring-clauses' },
        { label: "Roll Convention", path: '/admin/reference-data/roll-conventions' },
        { label: "Rounding Convention", path: '/admin/reference-data/rounding-conventions' },
        { label: "Schedule Stub Type", path: '/admin/reference-data/schedule-stub-types' },
        { label: "Schedule Type", path: '/admin/reference-data/schedule-types' },
        { label: "Seniority", path: '/admin/reference-data/seniorities' },
        { label: "Settlement Rate Option", path: '/admin/reference-data/settlement-rate-options' },
        { label: "Succession Event", path: '/admin/reference-data/succession-events' },
        { label: "Taxonomy", path: '/admin/reference-data/taxonomies' },
        { label: "Taxonomy Type", path: '/admin/reference-data/taxonomy-types' },
        { label: "Venue", path: '/admin/reference-data/venues' },
        { label: "Volatility Type", path: '/admin/reference-data/volatility-types' },
        ],
      },
      {
        label: "Identity",
        path: '/admin/identity/audit-logs',
        children: [
        { label: "Audit Log", path: '/admin/identity/audit-logs' },
        { label: "Group Page Permission", path: '/admin/identity/group-page-permissions' },
        { label: "Page", path: '/admin/identity/pages' },
        { label: "Permissions", path: '/admin/identity/permissions' },
        { label: "Role Permissions", path: '/admin/identity/role-permissions' },
        { label: "Roles", path: '/admin/identity/roles' },
        { label: "User Group", path: '/admin/identity/user-groups' },
        { label: "User Group Membership", path: '/admin/identity/user-group-memberships' },
        { label: "User Roles", path: '/admin/identity/user-roles' },
        { label: "User Sessions", path: '/admin/identity/user-sessions' },
        { label: "Users", path: '/admin/identity/users' },
        { label: "Workflow Definition", path: '/admin/identity/workflow-definitions' },
        { label: "Workflow History", path: '/admin/identity/workflow-histories' },
        { label: "Workflow Instance", path: '/admin/identity/workflow-instances' },
        { label: "Workflow Step", path: '/admin/identity/workflow-steps' },
        { label: "Workflow Task", path: '/admin/identity/workflow-tasks' },
        ],
      },
      {
        label: "Trading",
        path: '/admin/trading/allocation-instructions',
        children: [
        { label: "Allocation Instruction", path: '/admin/trading/allocation-instructions' },
        { label: "Allocation Line", path: '/admin/trading/allocation-lines' },
        { label: "Allocation Template", path: '/admin/trading/allocation-templates' },
        { label: "Backtest Exceptions", path: '/admin/trading/backtest-exceptions' },
        { label: "Backtest Results", path: '/admin/trading/backtest-results' },
        { label: "Bank Balances", path: '/admin/trading/bank-balances' },
        { label: "Best Execution Report", path: '/admin/trading/best-execution-reports' },
        { label: "Block Allocation", path: '/admin/trading/block-allocations' },
        { label: "Block Monitor Entry", path: '/admin/trading/block-monitor-entries' },
        { label: "Bond", path: '/admin/trading/bonds' },
        { label: "Bond Pick Candidates", path: '/admin/trading/bond-pick-candidates' },
        { label: "Bond Pick Constraint Results", path: '/admin/trading/bond-pick-constraint-results' },
        { label: "Bond Pick Exceptions", path: '/admin/trading/bond-pick-exceptions' },
        { label: "Bond Pick Runs", path: '/admin/trading/bond-pick-runs' },
        { label: "Bond Pick Trades", path: '/admin/trading/bond-pick-trades' },
        { label: "Bond Trade", path: '/admin/trading/bond-trades' },
        { label: "Broker Market Accesses", path: '/admin/trading/broker-market-accesses' },
        { label: "Brokers", path: '/admin/trading/brokers' },
        { label: "Cap Floor", path: '/admin/trading/cap-floors' },
        { label: "Cap Floor Notional Schedule", path: '/admin/trading/cap-floor-notional-schedules' },
        { label: "Cap Floor Schedule", path: '/admin/trading/cap-floor-schedules' },
        { label: "Cap Floor Trade", path: '/admin/trading/cap-floor-trades' },
        { label: "Cds Instrument", path: '/admin/trading/cds-instruments' },
        { label: "Cds Swaption", path: '/admin/trading/cds-swaptions' },
        { label: "Cds Trade", path: '/admin/trading/cds-trades' },
        { label: "Cds Tranche", path: '/admin/trading/cds-tranches' },
        { label: "Commodity Forward", path: '/admin/trading/commodity-forwards' },
        { label: "Commodity Option", path: '/admin/trading/commodity-options' },
        { label: "Commodity Trade", path: '/admin/trading/commodity-trades' },
        { label: "Contingent Order", path: '/admin/trading/contingent-orders' },
        { label: "Corporate Action", path: '/admin/trading/corporate-actions' },
        { label: "Corporate Action Election", path: '/admin/trading/corporate-action-elections' },
        { label: "Corporate Action Entitlement", path: '/admin/trading/corporate-action-entitlements' },
        { label: "Credit Event", path: '/admin/trading/credit-events' },
        { label: "Dividend Swap", path: '/admin/trading/dividend-swaps' },
        { label: "Eq Option Trade", path: '/admin/trading/eq-option-trades' },
        { label: "Equity", path: '/admin/trading/equities' },
        { label: "Equity Mifid2 Transaction Reports", path: '/admin/trading/equity-mifid2-transaction-reports' },
        { label: "Equity Price Adjusteds", path: '/admin/trading/equity-price-adjusteds' },
        { label: "Equity Price Raws", path: '/admin/trading/equity-price-raws' },
        { label: "Equity Short Position Reports", path: '/admin/trading/equity-short-position-reports' },
        { label: "Equity Swap Leg", path: '/admin/trading/equity-swap-legs' },
        { label: "Equity Trade", path: '/admin/trading/equity-trades' },
        { label: "Execution", path: '/admin/trading/executions' },
        { label: "Exercise Event", path: '/admin/trading/exercise-events' },
        { label: "Exotic Option", path: '/admin/trading/exotic-options' },
        { label: "Exotic Option Trade", path: '/admin/trading/exotic-option-trades' },
        { label: "Fill", path: '/admin/trading/fills' },
        { label: "Fix Message", path: '/admin/trading/fix-messages' },
        { label: "Fra", path: '/admin/trading/fras' },
        { label: "Future Positions", path: '/admin/trading/future-positions' },
        { label: "Futures Analytics Results", path: '/admin/trading/futures-analytics-results' },
        { label: "Futures Contract", path: '/admin/trading/futures-contracts' },
        { label: "Futures Margin Account Balances", path: '/admin/trading/futures-margin-account-balances' },
        { label: "Futures Margin Calls", path: '/admin/trading/futures-margin-calls' },
        { label: "Futures Position Limit Monitoring", path: '/admin/trading/futures-position-limit-monitorings' },
        { label: "Futures Span Im Position Details", path: '/admin/trading/futures-span-im-position-details' },
        { label: "Futures Span Im Results", path: '/admin/trading/futures-span-im-results' },
        { label: "Futures Stress Test Results", path: '/admin/trading/futures-stress-test-results' },
        { label: "Futures Vm Records", path: '/admin/trading/futures-vm-records' },
        { label: "Fx Cls Netting Sets", path: '/admin/trading/fx-cls-netting-sets' },
        { label: "Fx Credit Lines", path: '/admin/trading/fx-credit-lines' },
        { label: "Fx Instrument", path: '/admin/trading/fx-instruments' },
        { label: "Fx Master Agreements", path: '/admin/trading/fx-master-agreements' },
        { label: "Fx Ndf Fixing Monitors", path: '/admin/trading/fx-ndf-fixing-monitors' },
        { label: "Fx Option", path: '/admin/trading/fx-options' },
        { label: "Fx Option Trade", path: '/admin/trading/fx-option-trades' },
        { label: "Fx Options Greeks Snapshots", path: '/admin/trading/fx-options-greeks-snapshots' },
        { label: "Fx Pba Agreements", path: '/admin/trading/fx-pba-agreements' },
        { label: "Fx Pnl Attributions", path: '/admin/trading/fx-pnl-attributions' },
        { label: "Fx Position Snapshots", path: '/admin/trading/fx-position-snapshots' },
        { label: "Fx Risk Limit Monitorings", path: '/admin/trading/fx-risk-limit-monitorings' },
        { label: "Fx Saccr Results", path: '/admin/trading/fx-saccr-results' },
        { label: "Fx Settlement Fails", path: '/admin/trading/fx-settlement-fails' },
        { label: "Fx Spots", path: '/admin/trading/fx-spots' },
        { label: "Fx Trade", path: '/admin/trading/fx-trades' },
        { label: "Fx Window Election", path: '/admin/trading/fx-window-elections' },
        { label: "Hybrid Order", path: '/admin/trading/hybrid-orders' },
        { label: "Hybrid Order Constituent", path: '/admin/trading/hybrid-order-constituents' },
        { label: "Instrument", path: '/admin/trading/instruments' },
        { label: "Instrument Cashflow", path: '/admin/trading/instrument-cashflows' },
        { label: "Instrument Reset", path: '/admin/trading/instrument-resets' },
        { label: "Ir Swap Trade", path: '/admin/trading/ir-swap-trades' },
        { label: "Listed Options", path: '/admin/trading/option' },
        { label: "Loan Deposit Schedule", path: '/admin/trading/loan-deposit-schedules' },
        { label: "Manager Program", path: '/admin/trading/manager-programs' },
        { label: "Money Market", path: '/admin/trading/money-markets' },
        { label: "Monitor Group", path: '/admin/trading/monitor-groups' },
        { label: "Monitor Group Member", path: '/admin/trading/monitor-group-members' },
        { label: "Notional Schedule", path: '/admin/trading/notional-schedules' },
        { label: "Oms Allocation Instructions", path: '/admin/trading/oms-allocation-instructions' },
        { label: "Oms Allocation Lines", path: '/admin/trading/oms-allocation-lines' },
        { label: "Oms Allocation Templates", path: '/admin/trading/oms-allocation-templates' },
        { label: "Oms Audit Events", path: '/admin/trading/order-audit-event' },
        { label: "Oms Block Monitor Entries", path: '/admin/trading/oms-block-monitor-entries' },
        { label: "Oms Exceptions", path: '/admin/trading/oms-exceptions' },
        { label: "Oms Manager Programs", path: '/admin/trading/oms-manager-programs' },
        { label: "Oms Monitor Group Members", path: '/admin/trading/oms-monitor-group-members' },
        { label: "Oms Monitor Groups", path: '/admin/trading/oms-monitor-groups' },
        { label: "Oms Order Allocations", path: '/admin/trading/oms-order-allocations' },
        { label: "Oms Order Compliance Checks", path: '/admin/trading/oms-order-compliance-checks' },
        { label: "Order Placements", path: '/admin/trading/order-placements' },
        { label: "Oms Order Versions", path: '/admin/trading/oms-order-versions' },
        { label: "Oms Outbox Events", path: '/admin/trading/oms-outbox-events' },
        { label: "Oms Placement Templates", path: '/admin/trading/oms-placement-templates' },
        { label: "Oms Rationales", path: '/admin/trading/order-rationale' },
        { label: "Oms Trade Bookings", path: '/admin/trading/oms-trade-bookings' },
        { label: "Opt Bucket Defs", path: '/admin/trading/opt-bucket-defs' },
        { label: "Opt Bucket Impl Plans", path: '/admin/trading/opt-bucket-impl-plans' },
        { label: "Opt Bucket Members", path: '/admin/trading/opt-bucket-members' },
        { label: "Opt Bucket Return Series", path: '/admin/trading/opt-bucket-return-series' },
        { label: "Opt Bucket Rules", path: '/admin/trading/opt-bucket-rules' },
        { label: "Opt Bucket Stats", path: '/admin/trading/opt-bucket-stats' },
        { label: "Opt Constraint Rules", path: '/admin/trading/opt-constraint-rules' },
        { label: "Opt Constraint Sets", path: '/admin/trading/opt-constraint-sets' },
        { label: "Opt Model Covariances", path: '/admin/trading/opt-model-covariances' },
        { label: "Opt Model Returns", path: '/admin/trading/opt-model-returns' },
        { label: "Opt Run Asset Nodes", path: '/admin/trading/opt-run-asset-nodes' },
        { label: "Opt Run Weight Nodes", path: '/admin/trading/opt-run-weight-nodes' },
        { label: "Optimization Approvals", path: '/admin/trading/optimization-approvals' },
        { label: "Optimization Constraint Templates", path: '/admin/trading/optimization-constraint-templates' },
        { label: "Optimization Data Snapshots", path: '/admin/trading/optimization-data-snapshots' },
        { label: "Optimization Diagnostics", path: '/admin/trading/optimization-diagnostics' },
        { label: "Optimization Discrete Allocations", path: '/admin/trading/optimization-discrete-allocations' },
        { label: "Optimization Efficient Frontier Points", path: '/admin/trading/optimization-efficient-frontier-points' },
        { label: "Optimization Metrics", path: '/admin/trading/optimization-metrics' },
        { label: "Optimization Universe Assets", path: '/admin/trading/optimization-universe-assets' },
        { label: "Optimization Universes", path: '/admin/trading/optimization-universes' },
        { label: "Optimization Weights", path: '/admin/trading/optimization-weights' },
        { label: "Option", path: '/admin/trading/options' },
        { label: "Order", path: '/admin/trading/orders' },
        { label: "Order Algo Instruction", path: '/admin/trading/order-algo-instructions' },
        { label: "Order Allocation", path: '/admin/trading/order-allocations' },
        { label: "Order Audit Event", path: '/admin/trading/order-audit-events' },
        { label: "Order Automation Rule", path: '/admin/trading/order-automation-rules' },
        { label: "Order Blotter Profile", path: '/admin/trading/order-blotter-profiles' },
        { label: "Order Compliance Check", path: '/admin/trading/order-compliance-checks' },
        { label: "Order Compliance Reset Field", path: '/admin/trading/order-compliance-reset-fields' },
        { label: "Order Dual Auth Request", path: '/admin/trading/order-dual-auth-requests' },
        { label: "Order Exception", path: '/admin/trading/order-exceptions' },
        { label: "Order Outbox Event", path: '/admin/trading/order-outbox-events' },
        { label: "Order Rationale", path: '/admin/trading/order-rationales' },
        { label: "Order Routing Rules", path: '/admin/trading/routing-rule' },
        { label: "Order System Parameter", path: '/admin/trading/order-system-parameters' },
        { label: "Order System Parameter Override", path: '/admin/trading/order-system-parameter-overrides' },
        { label: "Order Version", path: '/admin/trading/order-versions' },
        { label: "Outbox Event", path: '/admin/trading/outbox-events' },
        { label: "Party Trade Role", path: '/admin/trading/party-trade-roles' },
        { label: "Placement Template", path: '/admin/trading/placement-templates' },
        { label: "Pledge Details", path: '/admin/trading/pledge-details' },
        { label: "Pnl Series", path: '/admin/trading/pnl-series' },
        { label: "Post Trade Routing Rule", path: '/admin/trading/post-trade-routing-rules' },
        { label: "Rebalance Sessions", path: '/admin/trading/rebalance-sessions' },
        { label: "Rebalance Trade Orders", path: '/admin/trading/rebalance-trade-orders' },
        { label: "Regulatory Submissions", path: '/admin/trading/regulatory-submissions' },
        { label: "Repo", path: '/admin/trading/repos' },
        { label: "Repo Schedule", path: '/admin/trading/repo-schedules' },
        { label: "Repo Trade", path: '/admin/trading/repo-trades' },
        { label: "Routing Rule", path: '/admin/trading/routing-rules' },
        { label: "Saved Search", path: '/admin/trading/saved-searches' },
        { label: "Schedule", path: '/admin/trading/schedules' },
        { label: "Sec Lend Trade", path: '/admin/trading/sec-lend-trades' },
        { label: "Securities Lending", path: '/admin/trading/securities-lendings' },
        { label: "Securities Lending Trades", path: '/admin/trading/sec-lend-trade' },
        { label: "Securitized", path: '/admin/trading/securitizeds' },
        { label: "Settlement Discipline Records", path: '/admin/trading/settlement-discipline-records' },
        { label: "Swap", path: '/admin/trading/swaps' },
        { label: "Swap Leg", path: '/admin/trading/swap-legs' },
        { label: "Swaption", path: '/admin/trading/swaptions' },
        { label: "Swaption Trade", path: '/admin/trading/swaption-trades' },
        { label: "Total Return Swap", path: '/admin/trading/total-return-swaps' },
        { label: "Trade", path: '/admin/trading/trades' },
        { label: "Trade Amendment", path: '/admin/trading/trade-amendments' },
        { label: "Trade Amendment History", path: '/admin/trading/trade-amendment-histories' },
        { label: "Trade Booking", path: '/admin/trading/trade-bookings' },
        { label: "Trade Cashflow", path: '/admin/trading/trade-cashflows' },
        { label: "Trade Cf", path: '/admin/trading/trade-cfs' },
        { label: "Trade Comment", path: '/admin/trading/trade-comments' },
        { label: "Trade Confirmation", path: '/admin/trading/trade-confirmations' },
        { label: "Trade Event", path: '/admin/trading/trade-events' },
        { label: "Trade Fee", path: '/admin/trading/trade-fees' },
        { label: "Trade Reset", path: '/admin/trading/trade-resets' },
        { label: "Trade Security Snapshot", path: '/admin/trading/trade-security-snapshots' },
        { label: "Trade Source", path: '/admin/trading/trade-sources' },
        { label: "Trade Version", path: '/admin/trading/trade-versions' },
        { label: "Trade Workflow", path: '/admin/trading/trade-workflows' },
        { label: "Trade Workflow Definition", path: '/admin/trading/trade-workflow-definitions' },
        { label: "Transaction Reports", path: '/admin/trading/transaction-reports' },
        { label: "Trs Trade", path: '/admin/trading/trs-trades' },
        { label: "User Grid Layout", path: '/admin/trading/user-grid-layouts' },
        { label: "Variance Swap", path: '/admin/trading/variance-swaps' },
        ],
      },
      {
        label: "Market Data",
        path: '/admin/market-data/atm-volatility-curves',
        children: [
        { label: "ATM Volatility Curve", path: '/admin/market-data/atm-volatility-curves' },
        { label: "Credit Curve Point", path: '/admin/market-data/credit-curve-points' },
        { label: "Credit Spread Curve", path: '/admin/market-data/credit-spread-curves' },
        { label: "Credit Spread Point", path: '/admin/market-data/credit-spread-points' },
        { label: "Credit Spread Snapshot", path: '/admin/market-data/credit-spread-snapshots' },
        { label: "Curve Calibration History", path: '/admin/market-data/curve-calibration-histories' },
        { label: "Curve Definition", path: '/admin/market-data/curve-definitions' },
        { label: "Curve Instrument", path: '/admin/market-data/curve-instruments' },
        { label: "Curve Overlay", path: '/admin/market-data/curve-overlays' },
        { label: "Curve Point", path: '/admin/market-data/curve-points' },
        { label: "Curve Snapshot", path: '/admin/market-data/curve-snapshots' },
        { label: "Default Probability", path: '/admin/market-data/default-probabilities' },
        { label: "Equity Price Adjusted", path: '/admin/market-data/equity-price-adjusteds' },
        { label: "Equity Price Raw", path: '/admin/market-data/equity-price-raws' },
        { label: "Fixing", path: '/admin/market-data/fixings' },
        { label: "Fx Spot", path: '/admin/market-data/fx-spots' },
        { label: "Market Data Currency", path: '/admin/market-data/market-data-currencies' },
        { label: "Market Data Set", path: '/admin/market-data/market-data-sets' },
        { label: "Market Quote", path: '/admin/market-data/market-quotes' },
        { label: "Recovery Rate", path: '/admin/market-data/recovery-rates' },
        { label: "Vol Surface", path: '/admin/market-data/vol-surfaces' },
        { label: "Vol Surface Point", path: '/admin/market-data/vol-surface-points' },
        { label: "Vol Surface Quote", path: '/admin/market-data/vol-surface-quotes' },
        { label: "Vol Surface Snapshot", path: '/admin/market-data/vol-surface-snapshots' },
        ],
      },
      {
        label: "Position Book",
        path: '/admin/position-book/abor-accruals',
        children: [
        { label: "Abor Accrual", path: '/admin/position-book/abor-accruals' },
        { label: "Abor Journal Entry", path: '/admin/position-book/abor-journal-entries' },
        { label: "Abor Journal Line", path: '/admin/position-book/abor-journal-lines' },
        { label: "Abor Nav Calculation", path: '/admin/position-book/abor-nav-calculations' },
        { label: "Abor Position", path: '/admin/position-book/abor-positions' },
        { label: "Abor Tax Lot", path: '/admin/position-book/abor-tax-lots' },
        { label: "Abor Trial Balance", path: '/admin/position-book/abor-trial-balances' },
        { label: "Abor Valuation", path: '/admin/position-book/abor-valuations' },
        { label: "Bank Balance", path: '/admin/position-book/bank-balances' },
        { label: "Book Definition", path: '/admin/position-book/book-definitions' },
        { label: "Book Mapping Rule", path: '/admin/position-book/book-mapping-rules' },
        { label: "Capital Call", path: '/admin/position-book/capital-calls' },
        { label: "Control Run", path: '/admin/position-book/control-runs' },
        { label: "Control Run Step", path: '/admin/position-book/control-run-steps' },
        { label: "Counterparty Master", path: '/admin/position-book/counterparty-masters' },
        { label: "Event Adjustment Detail", path: '/admin/position-book/event-adjustment-details' },
        { label: "Event Cash Detail", path: '/admin/position-book/event-cash-details' },
        { label: "Event Collateral Detail", path: '/admin/position-book/event-collateral-details' },
        { label: "Event Corporate Action Detail", path: '/admin/position-book/event-corporate-action-details' },
        { label: "Event Fx Detail", path: '/admin/position-book/event-fx-details' },
        { label: "Event Header", path: '/admin/position-book/event-headers' },
        { label: "Event Lifecycle", path: '/admin/position-book/event-lifecycles' },
        { label: "Event Trade Detail", path: '/admin/position-book/event-trade-details' },
        { label: "Event Valuation Detail", path: '/admin/position-book/event-valuation-details' },
        { label: "Fof Constituent", path: '/admin/position-book/fof-constituents' },
        { label: "Fof Risk Measure", path: '/admin/position-book/fof-risk-measures' },
        { label: "Fof Simulation", path: '/admin/position-book/fof-simulations' },
        { label: "Fof Simulation Fund", path: '/admin/position-book/fof-simulation-funds' },
        { label: "Fund Of Funds", path: '/admin/position-book/fund-of-fundses' },
        { label: "Future Position", path: '/admin/position-book/future-positions' },
        { label: "Fx Position Snapshot", path: '/admin/position-book/fx-position-snapshots' },
        { label: "Gl Account Mapping", path: '/admin/position-book/gl-account-mappings' },
        { label: "Gl Chart Of Accounts", path: '/admin/position-book/gl-chart-of-accountses' },
        { label: "Ibor Cash Balance", path: '/admin/position-book/ibor-cash-balances' },
        { label: "Ibor Cash Flow Projected", path: '/admin/position-book/ibor-cash-flow-projecteds' },
        { label: "Ibor Pending Event", path: '/admin/position-book/ibor-pending-events' },
        { label: "Ibor Position", path: '/admin/position-book/ibor-positions' },
        { label: "Ibor Position Detail", path: '/admin/position-book/ibor-position-details' },
        { label: "Ibor Transaction Ledger", path: '/admin/position-book/ibor-transaction-ledgers' },
        { label: "Lp Capital Call", path: '/admin/position-book/lp-capital-calls' },
        { label: "Nav History", path: '/admin/position-book/nav-histories' },
        { label: "Pbor Attribution Results", path: '/admin/position-book/pbor-attribution-results' },
        { label: "Pbor Composite Definitions", path: '/admin/position-book/pbor-composite-definitions' },
        { label: "Pbor Composite Memberships", path: '/admin/position-book/pbor-composite-memberships' },
        { label: "Pbor Gips Reports", path: '/admin/position-book/pbor-gips-reports' },
        { label: "Pbor Return Serieses", path: '/admin/position-book/pbor-return-serieses' },
        { label: "Pnl Series", path: '/admin/position-book/pnl-serieses' },
        { label: "Position", path: '/admin/position-book/positions' },
        { label: "Position Snapshot", path: '/admin/position-book/position-snapshots' },
        { label: "Position Transfer Rule", path: '/admin/position-book/position-transfer-rules' },
        { label: "Rbor Risk Factor Sensitivity", path: '/admin/position-book/rbor-risk-factor-sensitivities' },
        { label: "Rbor Risk Limit Monitor", path: '/admin/position-book/rbor-risk-limit-monitors' },
        { label: "Rbor Risk Snapshot", path: '/admin/position-book/rbor-risk-snapshots' },
        { label: "Rbor Stress Test Result", path: '/admin/position-book/rbor-stress-test-results' },
        { label: "Rbor Var Result", path: '/admin/position-book/rbor-var-results' },
        { label: "Recon Break", path: '/admin/position-book/recon-breaks' },
        { label: "Recon Break Resolution", path: '/admin/position-book/recon-break-resolutions' },
        { label: "Recon Run", path: '/admin/position-book/recon-runs' },
        { label: "Reconciliation Rule", path: '/admin/position-book/reconciliation-rules' },
        { label: "Settlement Account", path: '/admin/position-book/settlement-accounts' },
        { label: "Tax Lot", path: '/admin/position-book/tax-lots' },
        { label: "Waterfall Config", path: '/admin/position-book/waterfall-configs' },
        ],
      },
      {
        label: "Risk & Compliance",
        path: '/admin/risk-compliance/aggregated-greekses',
        children: [
        { label: "Aggregated Greeks", path: '/admin/risk-compliance/aggregated-greekses' },
        { label: "Backtest Exception", path: '/admin/risk-compliance/backtest-exceptions' },
        { label: "Backtest Result", path: '/admin/risk-compliance/backtest-results' },
        { label: "CCF Override", path: '/admin/risk-compliance/ccf-overrides' },
        { label: "CDS Curve", path: '/admin/risk-compliance/cds-curves' },
        { label: "Collateral Agreement", path: '/admin/risk-compliance/collateral-agreements' },
        { label: "Collateral Agreement Audit Log", path: '/admin/risk-compliance/collateral-agreement-audit-logs' },
        { label: "Collateral Allocation", path: '/admin/risk-compliance/collateral-allocations' },
        { label: "Collateral Margin Call Audit Log", path: '/admin/risk-compliance/collateral-margin-call-audit-logs' },
        { label: "Collateral Margin Call Settlement", path: '/admin/risk-compliance/collateral-margin-call-settlements' },
        { label: "Collateral Margin Call Settlement Security", path: '/admin/risk-compliance/collateral-margin-call-settlement-securities' },
        { label: "Collateral Movement", path: '/admin/risk-compliance/collateral-movements' },
        { label: "Collateral Position", path: '/admin/risk-compliance/collateral-positions' },
        { label: "Collateral Recall", path: '/admin/risk-compliance/collateral-recalls' },
        { label: "Collateral Substitution", path: '/admin/risk-compliance/collateral-substitutions' },
        { label: "Collateral Trend Data", path: '/admin/risk-compliance/collateral-trend-datas' },
        { label: "Collateral Type", path: '/admin/risk-compliance/collateral-types' },
        { label: "Compliance Breach", path: '/admin/risk-compliance/compliance-breaches' },
        { label: "Compliance Check", path: '/admin/risk-compliance/compliance-checks' },
        { label: "Compliance Rule", path: '/admin/risk-compliance/compliance-rules' },
        { label: "Concentration Limit", path: '/admin/risk-compliance/concentration-limits' },
        { label: "Correlation Matrices", path: '/admin/risk-compliance/correlation-matrices' },
        { label: "Correlation Matrix", path: '/admin/risk-compliance/correlation-matrixes' },
        { label: "Correlation Pair", path: '/admin/risk-compliance/correlation-pairs' },
        { label: "Correlation Stress Scenario", path: '/admin/risk-compliance/correlation-stress-scenarios' },
        { label: "Counterparty Exposures", path: '/admin/risk-compliance/counterparty-exposureses' },
        { label: "Counterparty Limit", path: '/admin/risk-compliance/counterparty-limits' },
        { label: "Credit Spread Curves", path: '/admin/risk-compliance/credit-spread-curves' },
        { label: "Credit Spread Points", path: '/admin/risk-compliance/credit-spread-points' },
        { label: "EAD Config", path: '/admin/risk-compliance/ead-configs' },
        { label: "Eligibility Rule", path: '/admin/risk-compliance/eligibility-rules' },
        { label: "Equity Mifid2Transaction Report", path: '/admin/risk-compliance/equity-mifid2-transaction-reports' },
        { label: "Equity Short Position Report", path: '/admin/risk-compliance/equity-short-position-reports' },
        { label: "Futures Analytics Result", path: '/admin/risk-compliance/futures-analytics-results' },
        { label: "Futures Margin Account Balance", path: '/admin/risk-compliance/futures-margin-account-balances' },
        { label: "Futures Margin Call", path: '/admin/risk-compliance/futures-margin-calls' },
        { label: "Futures Span Im Position Detail", path: '/admin/risk-compliance/futures-span-im-position-details' },
        { label: "Futures Span Im Result", path: '/admin/risk-compliance/futures-span-im-results' },
        { label: "Futures Stress Test Result", path: '/admin/risk-compliance/futures-stress-test-results' },
        { label: "Futures Vm Record", path: '/admin/risk-compliance/futures-vm-records' },
        { label: "Fx Cls Netting Set", path: '/admin/risk-compliance/fx-cls-netting-sets' },
        { label: "Fx Credit Line", path: '/admin/risk-compliance/fx-credit-lines' },
        { label: "Fx Master Agreement", path: '/admin/risk-compliance/fx-master-agreements' },
        { label: "Fx Options Greeks Snapshot", path: '/admin/risk-compliance/fx-options-greeks-snapshots' },
        { label: "Fx Pnl Attribution", path: '/admin/risk-compliance/fx-pnl-attributions' },
        { label: "Fx Risk Limit Monitoring", path: '/admin/risk-compliance/fx-risk-limit-monitorings' },
        { label: "Fx Saccr Result", path: '/admin/risk-compliance/fx-saccr-results' },
        { label: "Generic Credit Spreads", path: '/admin/risk-compliance/generic-credit-spreads' },
        { label: "Haircut Config", path: '/admin/risk-compliance/haircut-configs' },
        { label: "LGD Assumption", path: '/admin/risk-compliance/lgd-assumptions' },
        { label: "Margin Call", path: '/admin/risk-compliance/margin-calls' },
        { label: "Options Greeks", path: '/admin/risk-compliance/options-greekses' },
        { label: "PD Curve", path: '/admin/risk-compliance/pd-curves' },
        { label: "Pledge Detail", path: '/admin/risk-compliance/pledge-details' },
        { label: "Recovery Rate", path: '/admin/risk-compliance/recovery-rates' },
        { label: "Reg Frtb Config", path: '/admin/risk-compliance/reg-frtb-configs' },
        { label: "Reg Frtb Result", path: '/admin/risk-compliance/reg-frtb-results' },
        { label: "Reg Frtb Sensitivity Input", path: '/admin/risk-compliance/reg-frtb-sensitivity-inputs' },
        { label: "Reg Pnl Sensitivity Ladder", path: '/admin/risk-compliance/reg-pnl-sensitivity-ladders' },
        { label: "Reg Simm Config", path: '/admin/risk-compliance/reg-simm-configs' },
        { label: "Reg Simm Result", path: '/admin/risk-compliance/reg-simm-results' },
        { label: "Reg Simm Result Detail", path: '/admin/risk-compliance/reg-simm-result-details' },
        { label: "Reg Xva Config", path: '/admin/risk-compliance/reg-xva-configs' },
        { label: "Reg Xva Exposure Profile", path: '/admin/risk-compliance/reg-xva-exposure-profiles' },
        { label: "Reg Xva Result", path: '/admin/risk-compliance/reg-xva-results' },
        { label: "Reg Xva Sensitivity", path: '/admin/risk-compliance/reg-xva-sensitivities' },
        { label: "Reverse Stress Test Result", path: '/admin/risk-compliance/reverse-stress-test-results' },
        { label: "Risk Calculation", path: '/admin/risk-compliance/risk-calculations' },
        { label: "Risk Factor Mapping", path: '/admin/risk-compliance/risk-factor-mappings' },
        { label: "Risk Limit", path: '/admin/risk-compliance/risk-limits' },
        { label: "Risk Run Monitor", path: '/admin/risk-compliance/risk-run-monitors' },
        { label: "Risk Stress Test Result", path: '/admin/risk-compliance/risk-stress-test-results' },
        { label: "Security Pool", path: '/admin/risk-compliance/security-pools' },
        { label: "Stress Scenario", path: '/admin/risk-compliance/stress-scenarios' },
        { label: "Volatility Surfaces", path: '/admin/risk-compliance/volatility-surfaces' },
        { label: "Yield Curves", path: '/admin/risk-compliance/yield-curves' },
        ],
      },
      {
        label: "Operations",
        path: '/admin/operations/batch-definitions',
        children: [
        { label: "Batch Definition", path: '/admin/operations/batch-definitions' },
        { label: "Batch Run", path: '/admin/operations/batch-runs' },
        { label: "Batch Run Audit", path: '/admin/operations/batch-run-audits' },
        { label: "Batch Template", path: '/admin/operations/batch-templates' },
        { label: "Ca Election", path: '/admin/operations/ca-elections' },
        { label: "FIX Message", path: '/admin/operations/fix-messages' },
        { label: "Fee Accrual", path: '/admin/operations/fee-accruals' },
        { label: "Fee Calc Run", path: '/admin/operations/fee-calc-runs' },
        { label: "Fee Charge", path: '/admin/operations/fee-charges' },
        { label: "Fee Charge Allocation", path: '/admin/operations/fee-charge-allocations' },
        { label: "Fee Charge Line", path: '/admin/operations/fee-charge-lines' },
        { label: "Fee Component", path: '/admin/operations/fee-components' },
        { label: "Fee Dispute", path: '/admin/operations/fee-disputes' },
        { label: "Fee Invoice Header", path: '/admin/operations/fee-invoice-headers' },
        { label: "Fee Invoice Line", path: '/admin/operations/fee-invoice-lines' },
        { label: "Fee Override", path: '/admin/operations/fee-overrides' },
        { label: "Fee Performance Fee State", path: '/admin/operations/fee-performance-fee-states' },
        { label: "Fee Schedule", path: '/admin/operations/fee-schedules' },
        { label: "Fee Schedule Component", path: '/admin/operations/fee-schedule-components' },
        { label: "Fee Schedule Component Tier", path: '/admin/operations/fee-schedule-component-tiers' },
        { label: "Fee Structure", path: '/admin/operations/fee-structures' },
        { label: "Fee Structure Tier", path: '/admin/operations/fee-structure-tiers' },
        { label: "Fee Tax Rate Jurisdiction", path: '/admin/operations/fee-tax-rate-jurisdictions' },
        { label: "Fund Fee Assignment", path: '/admin/operations/fund-fee-assignments' },
        { label: "Fx Ndf Fixing Monitor", path: '/admin/operations/fx-ndf-fixing-monitors' },
        { label: "Fx Settlement Fail", path: '/admin/operations/fx-settlement-fails' },
        { label: "Inventory Report", path: '/admin/operations/inventory-reports' },
        { label: "Job Run", path: '/admin/operations/job-runs' },
        { label: "Mx Generated Message", path: '/admin/operations/mx-generated-messages' },
        { label: "Mx Incoming Message", path: '/admin/operations/mx-incoming-messages' },
        { label: "Regulatory Submission", path: '/admin/operations/regulatory-submissions' },
        { label: "Settlement Discipline Record", path: '/admin/operations/settlement-discipline-records' },
        { label: "Settlement Fail", path: '/admin/operations/settlement-fails' },
        { label: "Settlement Instruction", path: '/admin/operations/settlement-instructions' },
        { label: "Settlement Transfer", path: '/admin/operations/settlement-transfers' },
        { label: "Ssi Change History", path: '/admin/operations/ssi-change-histories' },
        { label: "Ssi Override Request", path: '/admin/operations/ssi-override-requests' },
        { label: "Ssi Selection Audit", path: '/admin/operations/ssi-selection-audits' },
        { label: "Transaction Report", path: '/admin/operations/transaction-reports' },
        ],
      },
      {
        label: "Analytics & Reporting",
        path: '/admin/analytics-reporting/asset-class-returns',
        children: [
        { label: "Asset Class Return", path: '/admin/analytics-reporting/asset-class-returns' },
        { label: "Benchmark Blend", path: '/admin/analytics-reporting/benchmark-blends' },
        { label: "Benchmark Blend Component", path: '/admin/analytics-reporting/benchmark-blend-components' },
        { label: "Benchmark Version", path: '/admin/analytics-reporting/benchmark-versions' },
        { label: "Bond Pick Candidate", path: '/admin/analytics-reporting/bond-pick-candidates' },
        { label: "Bond Pick Constraint Result", path: '/admin/analytics-reporting/bond-pick-constraint-results' },
        { label: "Bond Pick Exception", path: '/admin/analytics-reporting/bond-pick-exceptions' },
        { label: "Bond Pick Run", path: '/admin/analytics-reporting/bond-pick-runs' },
        { label: "Bond Pick Trade", path: '/admin/analytics-reporting/bond-pick-trades' },
        { label: "Calc Run Input Snapshot", path: '/admin/analytics-reporting/calc-run-input-snapshots' },
        { label: "Calculated Metric", path: '/admin/analytics-reporting/calculated-metrics' },
        { label: "Composite", path: '/admin/analytics-reporting/composites' },
        { label: "Composite Member", path: '/admin/analytics-reporting/composite-members' },
        { label: "Composite Return", path: '/admin/analytics-reporting/composite-returns' },
        { label: "Daily Return TS", path: '/admin/analytics-reporting/daily-return-tses' },
        { label: "Daily Returns", path: '/admin/analytics-reporting/daily-returns' },
        { label: "Dashboard Config", path: '/admin/analytics-reporting/dashboard-configs' },
        { label: "Derivative Lifecycle Event", path: '/admin/analytics-reporting/derivative-lifecycle-events' },
        { label: "Derivative Pl Explain", path: '/admin/analytics-reporting/derivative-pl-explains' },
        { label: "Discretion Review", path: '/admin/analytics-reporting/discretion-reviews' },
        { label: "Distribution", path: '/admin/analytics-reporting/distributions' },
        { label: "Exception Log", path: '/admin/analytics-reporting/exception-logs' },
        { label: "External Valuation", path: '/admin/analytics-reporting/external-valuations' },
        { label: "Factor Exposure Snapshot", path: '/admin/analytics-reporting/factor-exposure-snapshots' },
        { label: "Factor Model Cutover", path: '/admin/analytics-reporting/factor-model-cutovers' },
        { label: "Factor Return", path: '/admin/analytics-reporting/factor-returns' },
        { label: "Fi Decomposition", path: '/admin/analytics-reporting/fi-decompositions' },
        { label: "Firm Aum Snapshot", path: '/admin/analytics-reporting/firm-aum-snapshots' },
        { label: "Firm Definition", path: '/admin/analytics-reporting/firm-definitions' },
        { label: "Fund Classification Review", path: '/admin/analytics-reporting/fund-classification-reviews' },
        { label: "Gips Report", path: '/admin/analytics-reporting/gips-reports' },
        { label: "Methodology Registers", path: '/admin/analytics-reporting/methodology-registers' },
        { label: "Monthly Return TS", path: '/admin/analytics-reporting/monthly-return-tses' },
        { label: "Monthly Returns", path: '/admin/analytics-reporting/monthly-returns' },
        { label: "Opt Bucket Def", path: '/admin/analytics-reporting/opt-bucket-defs' },
        { label: "Opt Bucket Impl Plan", path: '/admin/analytics-reporting/opt-bucket-impl-plans' },
        { label: "Opt Bucket Member", path: '/admin/analytics-reporting/opt-bucket-members' },
        { label: "Opt Bucket Return Series", path: '/admin/analytics-reporting/opt-bucket-return-serieses' },
        { label: "Opt Bucket Rule", path: '/admin/analytics-reporting/opt-bucket-rules' },
        { label: "Opt Bucket Stat", path: '/admin/analytics-reporting/opt-bucket-stats' },
        { label: "Opt Constraint Rule", path: '/admin/analytics-reporting/opt-constraint-rules' },
        { label: "Opt Constraint Set", path: '/admin/analytics-reporting/opt-constraint-sets' },
        { label: "Opt Model Covariance", path: '/admin/analytics-reporting/opt-model-covariances' },
        { label: "Opt Model Return", path: '/admin/analytics-reporting/opt-model-returns' },
        { label: "Opt Run Asset Node", path: '/admin/analytics-reporting/opt-run-asset-nodes' },
        { label: "Opt Run Weight Node", path: '/admin/analytics-reporting/opt-run-weight-nodes' },
        { label: "Optimization Approval", path: '/admin/analytics-reporting/optimization-approvals' },
        { label: "Optimization Constraint Template", path: '/admin/analytics-reporting/optimization-constraint-templates' },
        { label: "Optimization Data Snapshot", path: '/admin/analytics-reporting/optimization-data-snapshots' },
        { label: "Optimization Diagnostic", path: '/admin/analytics-reporting/optimization-diagnostics' },
        { label: "Optimization Discrete Allocation", path: '/admin/analytics-reporting/optimization-discrete-allocations' },
        { label: "Optimization Efficient Frontier Point", path: '/admin/analytics-reporting/optimization-efficient-frontier-points' },
        { label: "Optimization Metric", path: '/admin/analytics-reporting/optimization-metrics' },
        { label: "Optimization Run", path: '/admin/analytics-reporting/optimization-runs' },
        { label: "Optimization Universe", path: '/admin/analytics-reporting/optimization-universes' },
        { label: "Optimization Universe Asset", path: '/admin/analytics-reporting/optimization-universe-assets' },
        { label: "Optimization Weight", path: '/admin/analytics-reporting/optimization-weights' },
        { label: "Perf Recon Break", path: '/admin/analytics-reporting/perf-recon-breaks' },
        { label: "Perf Recon Run", path: '/admin/analytics-reporting/perf-recon-runs' },
        { label: "Performance Close", path: '/admin/analytics-reporting/performance-closes' },
        { label: "Performance Run", path: '/admin/analytics-reporting/performance-runs' },
        { label: "Position Return", path: '/admin/analytics-reporting/position-returns' },
        { label: "Rebalance Session", path: '/admin/analytics-reporting/rebalance-sessions' },
        { label: "Rebalance Trade Order", path: '/admin/analytics-reporting/rebalance-trade-orders' },
        { label: "Reg Pnl Unexplained Analysis", path: '/admin/analytics-reporting/reg-pnl-unexplained-analysises' },
        { label: "Regulatory Report", path: '/admin/analytics-reporting/regulatory-reports' },
        { label: "Report Configuration", path: '/admin/analytics-reporting/report-configurations' },
        { label: "Report Run", path: '/admin/analytics-reporting/report-runs' },
        { label: "Report Template", path: '/admin/analytics-reporting/report-templates' },
        { label: "Residual Observation", path: '/admin/analytics-reporting/residual-observations' },
        { label: "Restatement History", path: '/admin/analytics-reporting/restatement-histories' },
        { label: "Scenario Approvals", path: '/admin/analytics-reporting/scenario-approvals' },
        { label: "Scenario Shock", path: '/admin/analytics-reporting/scenario-shocks' },
        { label: "Scenario Versions", path: '/admin/analytics-reporting/scenario-versions' },
        { label: "Scenarios", path: '/admin/analytics-reporting/scenarios' },
        ],
      },
      {
        label: "Integration",
        path: '/admin/integration/integration-config',
        children: [
        { label: "Configs", path: '/admin/integration/integration-config' },
        { label: "Data Mapping", path: '/admin/integration/data-mappings' },
        { label: "Integration Config", path: '/admin/integration/integration-configs' },
        { label: "Integration Log", path: '/admin/integration/integration-logs' },
        { label: "Logs", path: '/admin/integration/logs' },
        { label: "Mappings", path: '/admin/integration/data-mapping' },
        { label: "Schedule Config", path: '/admin/integration/schedule-configs' },
        { label: "Schedules", path: '/admin/integration/schedule-config' },
        ],
      },
      {
        label: "Accounting",
        path: '/admin/accounting/accounting-accounts',
        children: [
        { label: "Accounting Account", path: '/admin/accounting/accounting-accounts' },
        { label: "Accounting Posting", path: '/admin/accounting/accounting-postings' },
        { label: "Accounting Rule", path: '/admin/accounting/accounting-rules' },
        { label: "Fx Hedge Designation", path: '/admin/accounting/fx-hedge-designations' },
        { label: "Fx Hedge Effectiveness Test", path: '/admin/accounting/fx-hedge-effectiveness-tests' },
        { label: "Hedge Position", path: '/admin/accounting/hedge-positions' },
        ],
      },
    ],
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: <SettingsIcon />,
    children: [
      { label: 'Profile & Preferences', path: '/settings' },
      { label: 'Workflow Engine', path: '/settings/workflow-engine' },
      { label: 'Order Workflow (Legacy)', path: '/activity/order-workflow' },
      { label: 'Trade Workflow (Legacy)', path: '/activity/trade-workflow' },
      { label: 'Benchmark Management', path: '/admin/benchmarks' },
    ],
  },
  {
    id: 'help',
    label: 'Help & Support',
    icon: <HelpIcon />,
    children: [
      { label: 'Help Center', path: '/help' },
      { label: 'Documentation', path: '/documentation' },
    ],
  },
];
